import { DestroyRef, effect, inject, PLATFORM_ID, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPlatformBrowser, isPlatformServer, PlatformLocation } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';

import { RouteHelper } from '@helper/route-paths';
import { WindowDetectService } from '@service/window-detect.service';
import { LativError } from './lativ-error';
import { ErrorItem_Action } from '@proto/store/api/common/error';
import { ConfirmData, ConfirmDialogComponent } from '@common/confirm-dialog/confirm-dialog.component';
import { RouterHistoryService } from '@service/router-history.service';
import { MaintainComponent } from './maintain/maintain.component';
import { MetaData } from '@helper/meta-data';
import { environment } from '../../environments/environment';
import { AuthStateService } from '@service/auth-state.service';
import { LOCALES } from '../../environments/locales';
import { Constants } from '@helper/constants';
import { Toast } from '@helper/toast';
import { Strings } from '@helper/strings';

import { isEqual } from 'lodash-es';
import { filter } from 'rxjs';
import Bugsnag from '@bugsnag/js';

export abstract class BaseViewModel<T> {
  get apiResult(): Signal<T> {
    return this._apiResult.asReadonly();
  }

  get apiError(): Signal<LativError | undefined> {
    return this._apiError.asReadonly();
  }

  get pageTitle(): Signal<string> {
    return this._pageTitle.asReadonly();
  }

  readonly router = inject(Router);
  readonly destroyRef = inject(DestroyRef);
  readonly detectService = inject(WindowDetectService);
  readonly authS = inject(AuthStateService);
  // notify error action is done, use effect to monitor state
  readonly errorActionNotifier = signal(0);

  private readonly titleService = inject(Title);
  private readonly rHService = inject(RouterHistoryService);
  private readonly matDialog = inject(MatDialog);
  private readonly platformLocation = inject(PlatformLocation);
  private readonly _apiError: WritableSignal<LativError | undefined> = signal(undefined);
  private readonly _apiResult: WritableSignal<T>;
  private readonly pId: object = inject(PLATFORM_ID);
  private readonly _pageTitle = signal('');
  private readonly metaDataService = inject(Meta);
  private readonly snackBar = inject(MatSnackBar);
  private readonly showErrorInToast = signal(false);
  private readonly defaultMetaDescription = $localize`lativ 台灣網路服飾品牌，提供平價且高品質的衣服，線上可直接購買女裝、男裝、嬰兒裝、童裝、帆布鞋等`;
  private readonly defaultKeywords = $localize`lativ,國民服飾,帆布鞋,牛仔褲,衣服,羽絨`;
  private readonly actionDialogId = 'action-dialog';

  protected constructor(defaultValue: T, noRobotsIndex = false) {
    this._apiResult = signal(defaultValue, { equal: (a: T, b: T) => isEqual(a, b) });
    effect(() => {
      if (!this._apiError() || isPlatformServer(this.pId)) {
        // do not handle error in server side
        return;
      }
      this.handleApiError();
    });
    effect(() => {
      if (this.detectService.currentPath()) {
        this.setTitleByUrl(this.detectService.currentPath(), this.apiResult(), noRobotsIndex);
      }
    });
  }

  protected handleApiError(): void {
    if (this._apiError()?.status === HttpStatusCode.NotFound) {
      this.router.navigate([RouteHelper.home()]);
    } else {
      const detail = this._apiError()?.error?.details?.[0];
      if (this.showErrorInToast() && detail?.action === ErrorItem_Action.ACTION_NONE) {
        Toast.open(this.snackBar, detail.message);
        return;
      }
      if (detail && detail.action != ErrorItem_Action.RETRY) {
        if (detail.action === ErrorItem_Action.LOGOUT && this.matDialog.getDialogById(this.actionDialogId)) {
          // prevent multiple logout
          return;
        }
        if (detail.action === ErrorItem_Action.LOGOUT) {
          Bugsnag.notify(`logout user with dialog uid: ${this.authS.getIDToken()?.uid || ''}`);
        }
        ConfirmDialogComponent.open(
          this.matDialog,
          new ConfirmData({
            content: detail.message,
            singleBtnTitle: $localize`確定`,
          }),
          detail.action === ErrorItem_Action.LOGOUT ? this.actionDialogId : undefined
        )
          .afterClosed()
          .pipe(
            filter(res => !!res),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe({
            next: () => {
              switch (detail.action) {
                case ErrorItem_Action.POP: {
                  if (this.router.url.includes(RouteHelper.cart())) {
                    this.rHService.goRoute(RouteHelper.cart());
                  } else {
                    this.rHService.goBack();
                  }
                  break;
                }
                case ErrorItem_Action.LOGOUT: {
                  this.authS.logout();
                  break;
                }
                default:
                  break;
              }
              this.errorActionNotifier.update(v => v + 1);
            },
          });
      } else {
        MaintainComponent.open(this.matDialog)
          ?.afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.errorActionNotifier.update(v => v + 1));
      }
    }
  }

  abstract getMetaByUrl(segments: string[], model: T): MetaData | undefined;

  setApiResult(res: T): void {
    this._apiResult.set(res);
    this._apiError.set(undefined);
  }

  setApiError(error: HttpErrorResponse | undefined, toastOnly: boolean = false): void {
    this.showErrorInToast.set(toastOnly);
    this._apiError.set(error);
  }

  private setTitleByUrl(url: string, model: T, needNoIndex = false): void {
    const meta = this.getMetaByUrl(
      url
        .split('/')
        .filter(url => !!url)
        .filter(url => !LOCALES.includes(url)),
      model
    );
    if (meta?.scalable) {
      this.addOrUpdateMeta('name=viewport', {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1, maximum-scale=2, user-scalable=1, viewport-fit=cover',
      });
    } else {
      this.addOrUpdateMeta('name=viewport', {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0, viewport-fit=cover',
      });
    }
    if (needNoIndex) {
      this.addOrUpdateMeta('name=robots', {
        name: 'robots',
        content: 'noindex',
      });
    } else {
      this.removeMetaTag('name=robots');
    }
    if (meta !== undefined) {
      this.titleService.setTitle(meta.title);
      this._pageTitle.set(meta.title);
      if (isPlatformServer(this.pId)) {
        const title = meta.title ? `${meta.title} - ${Constants.PAGE_TITLE}` : Constants.PAGE_TITLE;
        const webUrl = `${Constants.LINK_URL_HOST}${this.platformLocation.pathname}`;
        this.addOrUpdateMeta('name=keywords', { name: 'keywords', content: this.defaultKeywords });
        this.addOrUpdateMeta('name=title', { name: 'title', content: title });
        this.addOrUpdateMeta('property=fb:app_id', { property: 'fb:app_id', content: environment.fbAppId });
        this.addOrUpdateMeta('property=og:site_name', { property: 'og:site_name', content: Constants.PAGE_TITLE });
        this.addOrUpdateMeta('property=og:title', { property: 'og:title', content: title });
        this.addOrUpdateMeta('property=og:type', { property: 'og:type', content: 'article' });
        this.addOrUpdateMeta('property=og:url', {
          property: 'og:url',
          content: webUrl,
        });
        this.addOrUpdateMeta('name=description', {
          name: 'description',
          content: meta.description || this.defaultMetaDescription,
        });
        this.addOrUpdateMeta('property=og:description', {
          property: 'og:description',
          content: meta.description || this.defaultMetaDescription,
        });
        this.addOrUpdateMeta('property=og:locale', {
          property: 'og:locale',
          content: 'zh_tw',
        });
        if (meta.image) {
          const img = `${environment.fileScheme}/${meta.image}`;
          const wh = Strings.getWidthHeightFromHash(meta.image);
          this.addOrUpdateMeta('name=image', { name: 'image', content: img });
          this.addOrUpdateMeta('property=og:image', { property: 'og:image', content: img });
          this.addOrUpdateMeta('property=og:image:alt', { property: 'og:image:alt', content: title });
          if (wh[0] && wh[1]) {
            this.addOrUpdateMeta('property=og:image:width', { property: 'og:image:width', content: wh[0].toString() });
            this.addOrUpdateMeta('property=og:image:height', {
              property: 'og:image:height',
              content: wh[1].toString(),
            });
          }
        }
        // app specific data
        const appSchemeUrl = this.platformLocation.pathname.includes('/page')
          ? `${Constants.APP_SCHEME_URL}/webview?url=${webUrl}`
          : `${Constants.APP_SCHEME_URL}${this.platformLocation.pathname}`;
        this.addOrUpdateMeta('property=al:web:url', { property: 'al:web:url', content: webUrl });
        this.addOrUpdateMeta('property=al:ios:url', { property: 'al:ios:url', content: appSchemeUrl });
        this.addOrUpdateMeta('property=al:ios:app_store_id', {
          property: 'al:ios:app_store_id',
          content: Constants.APPLE_APP_ID,
        });
        this.addOrUpdateMeta('property=al:ios:app_name', { property: 'al:ios:app_name', content: 'lativ' });
        this.addOrUpdateMeta('property=al:android:url', { property: 'al:android:url', content: appSchemeUrl });
        this.addOrUpdateMeta('property=al:android:package', {
          property: 'al:android:package',
          content: Constants.ANDROID_PACKAGE_NAME,
        });
        this.addOrUpdateMeta('property=al:android:app_name', { property: 'al:android:app_name', content: 'lativ' });
      }
    }
    if (isPlatformBrowser(this.pId)) {
      // add apple banner
      this.addOrUpdateMeta('name=apple-itunes-app', {
        name: 'apple-itunes-app',
        content: `app-id=${Constants.APPLE_APP_ID}, app-argument=${window.location.href}`,
      });
    }
  }

  private addOrUpdateMeta(key: string, def: MetaDefinition): void {
    if (!this.metaDataService.getTag(key)) {
      this.metaDataService.addTag(def);
    } else {
      this.metaDataService.updateTag(def);
    }
  }

  private removeMetaTag(tag: string): void {
    this.metaDataService.removeTag(tag);
  }
}
